import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Ltr, Row, Spinner } from '../components/ui.js';

interface JsmRequest {
  issueId: string;
  issueKey: string;
  currentStatus?: { status: string };
  requestFieldValues?: Array<{ fieldId: string; label: string; value: unknown }>;
  requestType?: { name: string };
  serviceDesk?: { projectName: string };
}

export function JsmPage(): JSX.Element {
  const { t } = useI18n();
  const [ownership, setOwnership] = useState('OWNED_REQUESTS');
  const [status, setStatus] = useState<'OPEN_REQUESTS' | 'CLOSED_REQUESTS' | 'ALL_REQUESTS'>(
    'OPEN_REQUESTS',
  );
  const [selected, setSelected] = useState<string | null>(null);

  const requests = useQuery({
    queryKey: ['jsm', ownership, status],
    queryFn: () =>
      api<{ items: JsmRequest[] }>(`/jsm/requests?ownership=${ownership}&status=${status}`),
  });

  const detail = useQuery({
    queryKey: ['jsm-detail', selected],
    queryFn: () =>
      api<{
        request: JsmRequest;
        sla: Array<{ id: string; name: string; ongoingCycle?: { breached?: boolean; remainingTime?: { friendly?: string } } }>;
        approvals: Array<{ id: string; name: string; finalDecision: string; canAnswerApproval: boolean }>;
        transitions: Array<{ id: string; name: string }>;
      }>(`/jsm/requests/${selected}`),
    enabled: selected !== null,
  });

  const summaryOf = (request: JsmRequest): string =>
    String(request.requestFieldValues?.find((f) => f.fieldId === 'summary')?.value ?? '');

  return (
    <div className="page">
      <div className="header">
        <h1>Service Management</h1>
      </div>

      <div className="scroll-x">
        {[
          ['OWNED_REQUESTS', t('home.reported')],
          ['PARTICIPATED_REQUESTS', t('home.watched')],
          ['APPROVER', t('home.approvals')],
          ['ORGANIZATION', 'Organization'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`chip ${ownership === value ? 'chip-indeterminate' : ''}`}
            onClick={() => setOwnership(value!)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="scroll-x">
        {(['OPEN_REQUESTS', 'CLOSED_REQUESTS', 'ALL_REQUESTS'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={`chip ${status === value ? 'chip-indeterminate' : ''}`}
            onClick={() => setStatus(value)}
          >
            {value.replace('_REQUESTS', '').toLowerCase()}
          </button>
        ))}
      </div>

      {requests.isLoading ? <Spinner /> : null}
      {requests.error ? <ErrorBox error={requests.error} /> : null}

      <Card>
        {(requests.data?.items ?? []).length === 0 ? <Empty /> : null}
        {(requests.data?.items ?? []).map((request) => (
          <Row
            key={request.issueKey}
            onClick={() => setSelected(request.issueKey)}
            title={
              <>
                <Ltr>{request.issueKey}</Ltr> · {summaryOf(request)}
              </>
            }
            subtitle={request.requestType?.name ?? request.serviceDesk?.projectName ?? ''}
            right={<span className="chip">{request.currentStatus?.status ?? ''}</span>}
          />
        ))}
      </Card>

      {selected ? (
        <>
          {detail.isLoading ? <Spinner /> : null}
          {detail.error ? <ErrorBox error={detail.error} /> : null}
          {detail.data ? (
            <>
              <Card title={selected}>
                <Row title={t('issue.status')} right={detail.data.request.currentStatus?.status ?? ''} />
                {detail.data.request.requestFieldValues?.map((field) => (
                  <Row
                    key={field.fieldId}
                    title={field.label}
                    right={String(field.value ?? '').slice(0, 120)}
                  />
                ))}
              </Card>

              {detail.data.sla.length > 0 ? (
                <Card title="SLA">
                  <div className="note">{t('notify.not_realtime')}</div>
                  {detail.data.sla.map((sla) => (
                    <Row
                      key={sla.id}
                      title={sla.name}
                      right={
                        <span
                          className={`chip ${sla.ongoingCycle?.breached ? 'chip-danger' : 'chip-done'}`}
                        >
                          {sla.ongoingCycle?.remainingTime?.friendly ?? '—'}
                        </span>
                      }
                    />
                  ))}
                </Card>
              ) : null}

              {detail.data.approvals.length > 0 ? (
                <Card title={t('home.approvals')}>
                  {detail.data.approvals.map((approval) => (
                    <ApprovalRow key={approval.id} issueKey={selected} approval={approval} />
                  ))}
                </Card>
              ) : null}

              <JsmComments issueKey={selected} />
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function ApprovalRow({
  issueKey,
  approval,
}: {
  issueKey: string;
  approval: { id: string; name: string; finalDecision: string; canAnswerApproval: boolean };
}): JSX.Element {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  const answer = async (decision: 'approve' | 'decline') => {
    setBusy(true);
    try {
      await api(`/jsm/requests/${issueKey}/approvals/${approval.id}`, {
        method: 'POST',
        body: { decision },
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="row" style={{ display: 'block' }}>
      <div className="row-title">{approval.name}</div>
      <div className="row-sub">{approval.finalDecision}</div>
      {approval.canAnswerApproval ? (
        <div className="btn-row" style={{ marginTop: 8 }}>
          <button type="button" className="btn" disabled={busy} onClick={() => void answer('approve')}>
            ✅ {t('common.yes')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy}
            onClick={() => void answer('decline')}
          >
            ✖ {t('common.no')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function JsmComments({ issueKey }: { issueKey: string }): JSX.Element {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  const comments = useQuery({
    queryKey: ['jsm-comments', issueKey],
    queryFn: () =>
      api<{
        items: Array<{ id: string; body: string; public: boolean; author?: { displayName: string } }>;
      }>(`/jsm/requests/${issueKey}/comments`),
  });

  return (
    <Card title={t('action.comment')}>
      <div className="field">
        <textarea value={text} onChange={(event) => setText(event.target.value)} />
      </div>
      <div className="toggle" style={{ marginBottom: 10 }}>
        <span>Public</span>
        <input
          type="checkbox"
          checked={isPublic}
          onChange={(event) => setIsPublic(event.target.checked)}
        />
      </div>
      <button
        type="button"
        className="btn btn-block"
        disabled={text.trim().length === 0}
        onClick={async () => {
          await api(`/jsm/requests/${issueKey}/comments`, {
            method: 'POST',
            body: { body: text, public: isPublic },
          });
          setText('');
          void comments.refetch();
        }}
      >
        {t('action.comment')}
      </button>

      {(comments.data?.items ?? []).map((comment) => (
        <div key={comment.id} className="row" style={{ display: 'block' }}>
          <div className="row-sub">
            <strong>{comment.author?.displayName ?? '—'}</strong>{' '}
            <span className={`chip ${comment.public ? '' : 'chip-danger'}`}>
              {comment.public ? 'public' : 'internal'}
            </span>
          </div>
          <div>{comment.body}</div>
        </div>
      ))}
    </Card>
  );
}
